import * as React from 'react';
import {arrayMove} from 'react-sortable-hoc';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import classNames from 'classnames';
import {Bubble, Button, Checkbox} from '@deviceinsight/ng-ui-basic-components';
import type {Property, Datapoint, Asset} from '@deviceinsight/ng-ui-api-client';
import SortableInfoItemConfigs from './SortableInfoItemConfigs';
import DatapointSearchComponent from './datapoints/DatapointSearchComponent';
import {getAllAssetProperties} from '../../../../api/properties';
import {isInfoWidgetItemValid} from './ConfigUtils';
import './InfoWidgetConfig.less';
import type {InfoWidgetItem} from '../types';
import {DEFAULT_SELECTABLE_DATATYPES} from './datapoints/columnDefs/datapoints';
import InputWithI18NInfo, {I18nBubbleContent} from '../../../common/InputWithI18NInfo';
import {WidgetConfigProps} from '@deviceinsight/ng-ui-scale-lib/api';

type Props = WidgetConfigProps;
type State = {
	selectedIndex: number;
	assetProperties: Array<Property>;
};
const headlineLabel = {
	id: 'dashboard.widgets.info.boxHeadline',
	defaultValue: 'Headline'
};
type IndexProps = {
	oldIndex: number;
	newIndex: number;
};
const checkBoxMessages = {
	paginated: {
		title: {
			id: 'dashboard.widgets.info.paginated.title',
			defaultValue: 'If this is selected, the values get paginated to a defined page size'
		},
		label: {
			id: 'dashboard.widgets.info.paginated.label',
			defaultValue: 'Paginated'
		}
	},
	filterable: {
		title: {
			id: 'dashboard.widgets.info.filterable.title',
			defaultValue: 'If this is selected, the paginated table can be filtered'
		},
		label: {
			id: 'dashboard.widgets.info.filterable.label',
			defaultValue: 'Filterable'
		}
	}
};
const EXTENDED_DATATYPES = [...DEFAULT_SELECTABLE_DATATYPES];
export default class InfoWidgetConfig extends React.Component<Props, State> {
	state = {
		selectedIndex: -1,
		assetProperties: []
	};

	componentDidMount() {
		getAllAssetProperties().then((properties) => {
			this.setState({
				assetProperties: properties
			});
		});
	}

	handleChangeSettings = (partialSettings: Record<string, any>) => {
		// I do not know why eslint complains here - same code see RuntimeAnalysisWidgetConfig without complaint...
		// SPP-1518 was created to fix this (https://jira.device-insight.com/browse/SPP-1518)
		const {config, onChangeConfig} = this.props;
		const updatedSettings = {...config.settings, ...partialSettings};
		onChangeConfig({...config, settings: updatedSettings});
		this.updateApplyButtonState(updatedSettings);
	};
	updateApplyButtonState = (settings: Record<string, any>) => {
		const {items, rows} = settings;
		const {setApplyButtonDisabled} = this.props;
		const validItems = items ? items.filter((item: InfoWidgetItem) => isInfoWidgetItemValid(item)) : [];
		setApplyButtonDisabled(validItems.length !== items.length || !this.rowsValid(rows));
	};
	rearrangeItems = ({oldIndex, newIndex}: IndexProps) => {
		const {
			config: {
				settings: {items}
			}
		} = this.props;
		const newItems = arrayMove(items, oldIndex, newIndex);
		this.handleChangeSettings({
			items: newItems
		});
	};
	showSelectionTable = (selectedIndex: number) => {
		this.setState({
			selectedIndex
		});
	};
	hideSelectionTable = () => {
		this.setState({
			selectedIndex: -1
		});
	};
	removeItem = (index: number) => {
		const {
			config: {
				settings: {items}
			}
		} = this.props;
		const updatedItems = [...items];
		updatedItems.splice(index, 1);
		this.handleChangeSettings({
			items: updatedItems
		});
	};
	addItem = () => {
		const {
			config: {
				settings: {items}
			}
		} = this.props;
		const newItem = {
			label: '',
			type: 'property',
			propertyKey: undefined
		};
		this.handleChangeSettings({
			items: [...items, newItem]
		});
	};
	onItemChange = (index: number, item: InfoWidgetItem) => {
		const {
			config: {
				settings: {items}
			}
		} = this.props;
		const newItems = [...items];
		newItems[index] = item;
		this.handleChangeSettings({
			items: newItems
		});
	};
	onDatapointSelect = (datapoint: Datapoint) => {
		const {selectedIndex} = this.state;
		const {
			config: {
				settings: {items}
			}
		} = this.props;
		const newItems = [...items];
		newItems[selectedIndex] = {...newItems[selectedIndex], datapoint};
		this.setState(
			{
				selectedIndex: -1
			},
			() =>
				this.handleChangeSettings({
					items: newItems
				})
		);
	};
	updateRows = (e: React.SyntheticEvent<HTMLInputElement>) => {
		const value = e.currentTarget.value;
		this.handleChangeSettings({
			rows: value
		});
	};
	rowsValid = (rows: number | null | undefined): boolean => {
		return rows && !isNaN(rows) && rows > 0 && rows < 100 ? true : false;
	};

	renderDefaultContent() {
		const {
			config: {
				settings: {items, headline, paginated, rows, filterable}
			},
			context
		} = this.props;
		const {assetProperties} = this.state;
		const rowsClass = classNames({
			standard: true,
			wide: true,
			field: true,
			error: !this.rowsValid(rows)
		});
		return (
			<div className="di form info-widget-config">
				<h2>
					<Trans i18nKey="dashboard.widgets.config.configuration">Configuration</Trans>
				</h2>
				<div>
					<InputWithI18NInfo
						label={headlineLabel}
						value={headline}
						onChange={(headline) =>
							this.handleChangeSettings({
								headline
							})
						}
					/>
				</div>
				<div className="info-widget-config-settings">
					<Checkbox
						value={paginated}
						title={checkBoxMessages.paginated.title}
						label={checkBoxMessages.paginated.label}
						onChange={() =>
							this.handleChangeSettings({
								paginated: !paginated,
								filterable: !paginated && filterable
							})
						}
					/>
					{paginated && (
						<Checkbox
							value={filterable}
							title={checkBoxMessages.filterable.title}
							label={checkBoxMessages.filterable.label}
							disabled={!paginated}
							onChange={() =>
								this.handleChangeSettings({
									filterable: !filterable
								})
							}
						/>
					)}
				</div>
				<div className="info-widget-config-settings">
					<div className={rowsClass}>
						<label>
							<Trans i18nKey="dashboard.widgets.info.config.rows">Number of Rows (1-99):</Trans>
						</label>
						<input
							type="number"
							value={rows}
							className="info-widget-rows"
							maxLength={2}
							disabled={!paginated}
							onChange={this.updateRows}
							style={{
								width: '55px'
							}}
						/>
					</div>
				</div>
				{items.length > 0 && (
					<table className="di reactable">
						<thead>
							<tr>
								<th>
									<Trans i18nKey="dashboard.widget.info.order">Order</Trans>
								</th>
								<th>
									<Trans i18nKey="dashboard.widget.info.label">Item Label (optional)</Trans>
									<Bubble content={I18nBubbleContent}>
										<span
											style={{
												cursor: 'pointer',
												color: 'blue'
											}}>
											(?)
										</span>
									</Bubble>
								</th>
								<th>
									<Trans i18nKey="dashboard.widget.info.type">Item Type</Trans>
								</th>
								<th>
									<Trans i18nKey="dashboard.widget.info.config">Item Config</Trans>
								</th>
								<th>&nbsp;</th>
							</tr>
						</thead>
						<SortableInfoItemConfigs
							items={items}
							showDatapointSelectionTable={this.showSelectionTable}
							removeItem={this.removeItem}
							onItemChange={this.onItemChange}
							assetProperties={assetProperties}
							helperClass="di infowidget-dragged-item"
							onSortEnd={this.rearrangeItems}
							lockAxis="y"
							context={context}
							lockToContainerEdges
							useDragHandle
						/>
					</table>
				)}
				<div className="info-widget-add-item">
					<Button onClick={this.addItem}>
						<Trans i18nKey="dashboard.widgets.info.addItem">Add Item</Trans>
					</Button>
				</div>
			</div>
		);
	}

	renderDatapointSelection(asset: Asset) {
		return (
			<div>
				<DatapointSearchComponent
					asset={asset}
					onDatapointSelect={this.onDatapointSelect}
					availableDataTypes={EXTENDED_DATATYPES}
				/>
				<Button onClick={this.hideSelectionTable}>
					<Trans i18nKey="button.cancel">Cancel</Trans>
				</Button>
			</div>
		);
	}

	render() {
		const {context} = this.props;
		const asset = context.type === 'asset' ? context.asset : undefined;

		if (asset) {
			const {selectedIndex} = this.state;
			return selectedIndex > -1 ? this.renderDatapointSelection(asset) : this.renderDefaultContent();
		} else {
			// should never happen....
			return <div>Widget cannot be rendered - no asset was set in context</div>;
		}
	}
}
